/**
 * navStructure — single source of truth for the left-nav sidebar.
 *
 * Both Nav.tsx (rendering) and useKeyboardShortcuts.ts (the "g + letter"
 * chord handler) consume buildNavGroups(), so a displayed quick key and the
 * chord that actually fires can never drift apart.
 *
 * 5-group / 12-item structure (William nav redesign 2026-05-22; Operations
 * rename + Member Services removal 2026-05-24; Additional Programs added
 * 2026-05-24 rev31; Survey & Sentiment added 2026-05-24 rev32).
 * Every item carries a unique single-letter `shortcut` — the chord is `g`
 * then that letter.
 *
 *   g c  Campaign Details        g i  Interactive & Predictive
 *   g e  Members & Eligibility   g s  Clinical Support
 *   g l  Member & Provider Svcs   g y  Survey & Sentiment
 *   g f  Flight Deck             g t  Member Tracker
 *   g d  Operations              g r  Reports & Exports
 *                                g a  Ad-Hoc Dispatch
 *                                g p  Additional Programs
 */

export interface NavItem {
  /** Stable page identifier for per-campaign visibility (Campaign.hiddenPages).
   * Matches the route's final path segment where applicable. */
  key: string;
  path: string;
  num: string;
  label: string;
  /** Single letter — the chord is `g` then this key. Unique across the nav. */
  shortcut: string;
  /** PHI sensitivity tier badge, if any. */
  tier?: 2 | 3;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Build the nav groups for a given program prefix (e.g. "/fobt").
 * `programPrefix` scopes per-program routes; top-level routes ignore it.
 */
export function buildNavGroups(
  programPrefix: string,
  hiddenKeys?: Iterable<string>,
): NavGroup[] {
  const groups: NavGroup[] = [
    {
      label: 'SETUP',
      items: [
        { key: 'setup', path: '/setup', num: '01', label: 'Campaign Details', shortcut: 'C' },
        { key: 'population', path: `${programPrefix}/population`, num: '02', label: 'Members & Eligibility', shortcut: 'E' },
        { key: 'document-library', path: `${programPrefix}/document-library`, num: '03', label: 'Member & Provider Services', shortcut: 'L' },
      ],
    },
    {
      label: 'METRICS',
      items: [
        { key: 'flight-deck', path: '/flight-deck', num: '04', label: 'Flight Deck', shortcut: 'F' },
        { key: 'distribution', path: `${programPrefix}/distribution`, num: '05', label: 'Operations', shortcut: 'D' },
        { key: 'predictive', path: `${programPrefix}/predictive`, num: '06', label: 'Interactive & Predictive', shortcut: 'I' },
        { key: 'clinical', path: `${programPrefix}/clinical`, num: '07', label: 'Clinical Support', shortcut: 'S', tier: 3 },
        { key: 'sentiment', path: `${programPrefix}/sentiment`, num: '08', label: 'Survey & Sentiment', shortcut: 'Y', tier: 2 },
      ],
    },
    {
      label: 'OUTCOMES',
      items: [
        { key: 'member-tracker', path: `${programPrefix}/operations/member-lookup`, num: '09', label: 'Member Tracker', shortcut: 'T', tier: 2 },
        { key: 'reports', path: '/reports', num: '10', label: 'Reports & Exports', shortcut: 'R' },
      ],
    },
    {
      label: 'ON DEMAND SERVICES',
      items: [
        { key: 'ad-hoc-dispatch', path: `${programPrefix}/ad-hoc-dispatch`, num: '11', label: 'Ad-Hoc Dispatch', shortcut: 'A' },
      ],
    },
    {
      label: 'ADDITIONAL PROGRAMS',
      items: [
        { key: 'add-ons', path: '/add-ons', num: '12', label: 'Additional Programs', shortcut: 'P' },
      ],
    },
  ];

  const hidden = new Set(hiddenKeys ?? []);
  if (hidden.size === 0) return groups;
  // Drop hidden items; drop any group left with no visible items.
  return groups
    .map((g) => ({ ...g, items: g.items.filter((it) => !hidden.has(it.key)) }))
    .filter((g) => g.items.length > 0);
}

/** Flat { letter: path } map for the keyboard-chord handler. Honors the same
 *  per-campaign hidden-page filter as the rendered nav, so a chord can't jump
 *  to a page the sidebar hides. */
export function buildShortcutMap(
  programPrefix: string,
  hiddenKeys?: Iterable<string>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const group of buildNavGroups(programPrefix, hiddenKeys)) {
    for (const item of group.items) {
      if (item.shortcut) map[item.shortcut.toLowerCase()] = item.path;
    }
  }
  return map;
}
